import { useMemo } from 'react';
import { useWorldStore } from '../state/worldStore';
import { useVenueAnchors } from './useVenueAnchors';
import { ROOMS } from '../rooms';

// A venue's FULL transform — where it stands, how big it is, which way it faces.
//
// useVenueAnchors already owns position (sampler → anchorMesh → saved → draft).
// This adds the other two halves of the same decision and hands back one object,
// so a caller places a venue without re-deriving any of it.
//
// It exists because the precedence chain had started to be copy-pasted:
// MeshInterior worked it out to place the room, the footprint overlay to draw
// the outline, the brush to draw the ghost. Three copies is three chances to
// disagree, and when they disagree the symptom is a shell that does not line up
// with the interior inside it — which reads as a modelling problem rather than
// as the arithmetic bug it is.
//
// PRECEDENCE for scale and facing, most specific first:
//   ?venuerot=       explicit URL override, for debugging a facing
//   draft            the placement tool's unsaved edit, so the world responds
//                    while you drag the slider rather than after a save
//   saved override   the hand-placed anchor (world_objects, kind venue_anchor)
//   rooms.js         the authored default
export function useVenuePlacement(roomId) {
  const anchors = useVenueAnchors();
  const overrides = useWorldStore((s) => s.venueAnchorOverrides);
  const drafts = useWorldStore((s) => s.venuePlaceDraft);

  return useMemo(() => {
    const room = ROOMS[roomId] || {};
    const a = anchors?.[roomId];
    const d = drafts?.[roomId];
    const o = overrides?.[roomId];
    const q = typeof window !== 'undefined'
      ? parseFloat(new URLSearchParams(window.location.search).get('venuerot'))
      : NaN;

    return {
      // null until the city has reported its buildings. Callers should render
      // nothing rather than fall back to the origin, which would stand a shop in
      // the middle of the map for a frame.
      // Y is a MANUAL nudge, not a measurement. MeshInterior and the shell both
      // auto-ground a model by dropping its lowest point to zero, which is right
      // only when that point is the floor — a foundation, a kerb or one stray
      // vertex under the building lifts the visible floor off the street
      // instead, and nothing in the geometry says which it is. So this rides on
      // top of the auto-grounding rather than replacing it: 0 means "trust the
      // measurement", anything else is the correction for this model.
      pos: a ? [a[0], d?.y ?? o?.y ?? 0, a[2]] : null,
      scale: d?.scale ?? o?.scale ?? 1,
      rotY: Number.isFinite(q)
        ? (q * Math.PI) / 180
        : (d?.rotY ?? o?.rotY ?? room.modelRotY ?? 0),
      sited: room.placement === 'sited',
    };
  }, [roomId, anchors, overrides, drafts]);
}
